// src/pages/chat/fullscreen-recording-dock/fullscreen-recording-dock.tsx
import React from 'react';
import { createPortal } from 'react-dom';
import { DockShell } from '../dock/dock.styled';
import { DockIcon, DockLabel } from '../dock/record-dock/record-dock.styled';
import type { CtaHandle } from '../CTA/cta';
import { useRecordingControls } from './fullscreen-recording-dock.hooks';
import {
  RecordingBottom,
  ToggleLayer,
  ToggleSlot,
  DockItem,
} from './fullscreen-recording-dock.styled';

// ✅ Reuse the exact modal styles from record dock (so it looks identical)
import {
  ModalOverlay,
  ModalContent,
  RerecordConfirmation,
  DiscardAndReRecordTitleSpan,
  DiscardAndReRecordDescriptionSpan,
  CTAWrapper,
  CancelButton,
  ReRecordButton,
} from '../dock/record-dock/record-dock.styled';

export const FullScreenRecordingDock: React.FC<{
  ctaRef: React.RefObject<CtaHandle | null>;
  isPaused: boolean;
  onStopTap?: () => void;
}> = ({ ctaRef, isPaused, onStopTap }) => {
  const { pause, resume, stop, reRecord } = useRecordingControls({ ctaRef });

  const [showReRecordModal, setShowReRecordModal] = React.useState(false);
  const wasPausedBeforeModalRef = React.useRef(false);

  const openReRecordConfirm = React.useCallback(() => {
    wasPausedBeforeModalRef.current = isPaused;
    // ✅ match RecordDock behavior: pause before asking
    pause();
    setShowReRecordModal(true);
  }, [isPaused, pause]);

  const closeReRecordConfirm = React.useCallback(() => {
    setShowReRecordModal(false);
    // ✅ if user wasn't paused before we opened, resume when cancelling
    if (!wasPausedBeforeModalRef.current) {
      resume();
    }
  }, [resume]);

  const confirmReRecord = React.useCallback(() => {
    setShowReRecordModal(false);
    // ✅ discard + start a fresh recording, staying in fullscreen
    reRecord();
  }, [reRecord]);

  return (
    <>
      <DockShell style={{ gap: 0 }}>
          <RecordingBottom>
            <ToggleSlot>
              <ToggleLayer $active={isPaused}>
                <DockItem type="button" onClick={resume}>
                  <DockIcon src="/icons/onboarding/question/resume.svg" alt="Resume" />
                  <DockLabel>Resume</DockLabel>
                </DockItem>
              </ToggleLayer>

              <ToggleLayer $active={!isPaused}>
                <DockItem type="button" onClick={pause}>
                  <DockIcon src="/icons/onboarding/question/pause.svg" alt="Pause" />
                  <DockLabel>Pause</DockLabel>
                </DockItem>
              </ToggleLayer>
            </ToggleSlot>

            <DockItem
              type="button"
              onClick={() => {
                if (onStopTap) onStopTap();
                else stop();
              }}
            >
              <DockIcon src="/icons/onboarding/question/stop-button.svg" alt="Stop" />
            </DockItem>

            <DockItem type="button" onClick={openReRecordConfirm}>
              <DockIcon src="/icons/onboarding/question/re-record.svg" alt="Re-record" />
              <DockLabel>Re-record</DockLabel>
            </DockItem>
          </RecordingBottom>
      </DockShell>

      {/* ✅ Re-record modal — portalled to body to escape stacking context */}
      {createPortal(
        <ModalOverlay visible={showReRecordModal} onClick={closeReRecordConfirm}>
          <ModalContent visible={showReRecordModal} onClick={(e) => e.stopPropagation()}>
            <RerecordConfirmation className={showReRecordModal ? 'visible' : ''}>
              <DiscardAndReRecordTitleSpan>Discard and re-record?</DiscardAndReRecordTitleSpan>
              <DiscardAndReRecordDescriptionSpan>
                {"You'll begin again. Just remember it doesn't have to be perfect. Stream of consciousness is just fine."}
              </DiscardAndReRecordDescriptionSpan>

              <CTAWrapper>
                <CancelButton onClick={closeReRecordConfirm}>Cancel</CancelButton>

                <ReRecordButton onClick={confirmReRecord}>Re-record</ReRecordButton>
              </CTAWrapper>
            </RerecordConfirmation>
          </ModalContent>
        </ModalOverlay>,
        document.body
      )}
    </>
  );
};
